// Rediger en ferdig utfylt e-post slik den ser ut, uten å se HTML-en.
//
// Hvorfor ikke Quill (som «Send e-post manuelt» bruker) eller CKEditor: e-posten
// her har plassholderne fylt inn, så den inneholder ordretabellen og den
// grønne «Betal nå»-knappen. Begge editorene bygger innholdet om til sitt eget
// format og kaster tabeller og innebygde stiler — e-posten ble ødelagt bare av
// å åpnes. Et contentEditable-felt lar nettleseren redigere HTML-en på stedet,
// så alt admin ikke rører står igjen som det var.
import { useEffect, useRef } from "react";
import PropTypes from "prop-types";

import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";

import SoftBox from "components/SoftBox";

const LINE = "#e6ecf3";

const TOOLS = [
  { command: "bold", label: "Fet", Icon: FormatBoldIcon },
  { command: "italic", label: "Kursiv", Icon: FormatItalicIcon },
  { command: "underline", label: "Understreket", Icon: FormatUnderlinedIcon },
  { command: "insertUnorderedList", label: "Punktliste", Icon: FormatListBulletedIcon },
  { command: "removeFormat", label: "Fjern formatering", Icon: FormatClearIcon },
  { command: "undo", label: "Angre", Icon: UndoIcon },
  { command: "redo", label: "Gjør om", Icon: RedoIcon },
];

function HtmlEmailEditor({ value, onChange, maxHeight }) {
  const ref = useRef(null);
  // Det feltet sist meldte fra om. Kommer samme verdi tilbake som `value`, er
  // det vårt eget tastetrykk — å skrive innerHTML på nytt da flytter markøren
  // til starten for hvert tegn.
  const emitted = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || value === emitted.current) return;
    el.innerHTML = value || "";
    emitted.current = value;
  }, [value]);

  // Meldes bare ved faktisk redigering. Nettleseren skriver HTML-en litt om når
  // den leses tilbake, så en e-post admin ikke har rørt sendes som den kom.
  const emit = () => {
    const html = ref.current?.innerHTML ?? "";
    emitted.current = html;
    onChange(html);
  };

  const run = (command) => {
    ref.current?.focus();
    // execCommand er merket utgått, men er fortsatt det eneste som redigerer
    // et contentEditable-felt med angrehistorikk i alle nettlesere.
    document.execCommand(command, false, null);
    emit();
  };

  return (
    <SoftBox sx={{ border: `1px solid ${LINE}`, borderRadius: 2, backgroundColor: "#fff", overflow: "hidden" }}>
      <SoftBox
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          p: 0.5,
          borderBottom: `1px solid ${LINE}`,
          backgroundColor: "#f8fafc",
        }}
      >
        {TOOLS.map(({ command, label, Icon }) => (
          <button
            key={command}
            type="button"
            title={label}
            aria-label={label}
            // mousedown, ikke click: et klikk flytter fokus til knappen, og da
            // er markeringen i teksten borte før kommandoen kjører.
            onMouseDown={(e) => {
              e.preventDefault();
              run(command);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 30,
              height: 30,
              border: "none",
              borderRadius: 6,
              background: "transparent",
              color: "#344767",
              cursor: "pointer",
            }}
          >
            <Icon sx={{ fontSize: 18 }} />
          </button>
        ))}
      </SoftBox>

      <SoftBox
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label="Innhold i e-posten"
        onInput={emit}
        sx={{
          p: 2,
          maxHeight,
          overflowY: "auto",
          fontSize: 14,
          outline: "none",
          cursor: "text",
          "&:focus": { boxShadow: "inset 0 0 0 2px #cfe3f7" },
          "& p": { margin: "0 0 10px" },
        }}
      />
    </SoftBox>
  );
}

HtmlEmailEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  maxHeight: PropTypes.number,
};

HtmlEmailEditor.defaultProps = { value: "", maxHeight: 380 };

export default HtmlEmailEditor;
